/**
 * Theme Configuration
 * Extends shared theme definitions with web-specific properties (fonts).
 * Color data comes from @parlay-picker/shared/themes (single source of truth).
 */

import {
  DEFAULT_THEME_ID,
  type SharedTheme,
  themes as sharedThemes,
  type ThemeColors,
} from '@parlay-picker/shared/themes';

export type { ThemeColors };

export interface Theme extends SharedTheme {
  fonts?: {
    heading: string;
    body: string;
    mono: string;
  };
}

/** Web-specific font overrides for themes that use custom fonts */
const WEB_FONTS: Record<string, Theme['fonts']> = {
  'arctic-cyan': {
    heading: "'Space Grotesk', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },
  'crimson-edge': {
    heading: "'Space Grotesk', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },
  'emerald-prestige': {
    heading: "'Space Grotesk', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },
  'high-contrast': {
    heading: "'Space Grotesk', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },
  'light-court': {
    heading: "'Space Grotesk', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },
};

/** Build web themes by merging shared colors with web-specific fonts */
export const themes: Record<string, Theme> = Object.fromEntries(
  Object.entries(sharedThemes).map(([id, shared]) => [
    id,
    {
      ...shared,
      fonts: WEB_FONTS[id],
    },
  ]),
);

export function getTheme(themeId: string): Theme {
  return themes[themeId] || themes[DEFAULT_THEME_ID];
}

export function getThemeList(): Theme[] {
  return Object.values(themes);
}
